/**
 * AI event creator ("Create with AI") — endpoints of modules/event-assistant.
 * The chat only builds a DRAFT (shown in the live preview); the real event is
 * created by materialize(), triggered by the user's click.
 */
import { http, httpUpload } from "./client";

export interface AssistantAttachment {
  key: string;
  filename: string;
  contentType: string;
  size: number;
}

export interface AssistantDraftPosition {
  positionId?: string;
  positionName?: string;
  isBackupPosition?: boolean;
  startDate?: string;
  timeIn?: string;
  timeOut?: string;
  overNightShift?: boolean;
  numberOfHours?: number | null;
  quantity?: number;
  selectOption?: string; // meal option
  attire?: string;
  complexity?: string;
  additionalComments?: string;
  PPE_Requirements?: string;
}

export interface AssistantBillingEmail {
  name?: string;
  email?: string;
  mobile?: string;
}

export interface AssistantDraftSession {
  /** Id of the session already saved for the linked event (wizard or a previous sync). Absent = new. */
  id?: string;
  sessionName?: string;
  sessionStart?: string;
  sessionEnd?: string;
  sessionContactPoint?: string;
  mobile?: string;
  poNumber?: string;
  meetingLocation?: string;
  sameLocAsEvent?: boolean;
  venue?: string;
  venueAddressLine1?: string;
  venueAddressLine2?: string;
  venueCity?: string;
  venueState?: string;
  venueZipCode?: string;
  positions?: AssistantDraftPosition[];
}

export interface AssistantDraft {
  contactInfo?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    mobile?: string;
    company?: string;
    companyAddress?: string;
    isSameAddress?: boolean;
    COIAddress?: string;
    billingEmails?: {
      estimate?: AssistantBillingEmail[];
      invoice?: AssistantBillingEmail[];
      depositInvoice?: AssistantBillingEmail[];
    };
  };
  selectedClientId?: string | null;
  eventInfo?: {
    eventName?: string;
    eventStart?: string;
    eventEnd?: string;
    venue?: string;
    venueAddressLine1?: string;
    venueAddressLine2?: string;
    venueCity?: string;
    venueState?: string;
    venueZipCode?: string;
    eventTimezone?: string;
  };
  sessions?: AssistantDraftSession[];
  meta?: { missing?: string[]; warnings?: string[] };
}

/** POST event-assistant/chat response. */
export interface AssistantChatResponse {
  success: boolean;
  message: string;
  conversationId: string;
  status: "building" | "materialized" | "abandoned";
  draft: AssistantDraft;
  missing: string[];
  /** The draft event is saved (created/updated) after this turn — same partial save as "Back to wizard". */
  saved?: boolean;
  savedEventId?: string;
  /** Set when the auto-save after this turn failed (the answer still went through). */
  saveMessage?: string;
  /** What the auto-save could NOT write yet (incomplete positions/sessions) — the preview shows more than the wizard. */
  skipped?: string[];
  /** Diagnostics of the turn (model rounds used, last stop reason, replies cut by the output limit). */
  rounds?: number;
  stopReason?: string;
  cuts?: number;
}

/** Progress of the chat turn in flight — GET conversations/progress, polled while sending. */
export interface AssistantTurnProgress {
  stage: "thinking" | "model" | "tool" | "draft" | "cut" | "saving" | "done" | "error";
  round?: number;
  maxRounds?: number;
  tool?: string;
  /** Sync step in progress (stage "saving"), e.g. "createSession[1]". */
  step?: string;
  tokensOut?: number;
  sessions?: number;
  shifts?: number;
  message?: string;
  startedAt?: string;
  updatedAt?: string;
}

export interface AssistantProgressResponse {
  success: boolean;
  data: {
    conversationId: string;
    status: "building" | "materialized" | "abandoned";
    progress: AssistantTurnProgress | null;
    lastSyncError: string | null;
  } | null;
}

/** Caller-owned; without an id the backend answers for the latest building conversation (first turn). */
export const getAssistantProgress = (conversationId?: string | null) =>
  http({
    method: "GET",
    url: "event-assistant/conversations/progress",
    params: conversationId ? { conversationId } : undefined,
    isToken: true,
  });

export interface AssistantChatMessage {
  role: "user" | "ai";
  content: string;
  attachments?: AssistantAttachment[];
  createdAt?: string;
}

export const sendAssistantChat = (payload: {
  message: string;
  conversationId?: string;
  startNew?: boolean;
  attachments?: AssistantAttachment[];
}) =>
  http({
    method: "POST",
    url: "event-assistant/chat",
    data: {
      message: payload.message,
      ...(payload.conversationId ? { conversationId: payload.conversationId } : {}),
      ...(payload.startNew ? { startNew: true } : {}),
      ...(payload.attachments?.length ? { attachments: payload.attachments } : {}),
    },
    isToken: true,
  });

export const uploadAssistantAttachment = (file: File) => {
  const fd = new FormData();
  fd.append("file", file);
  return httpUpload("event-assistant/upload", fd, "POST");
};

/** A saved contact/event document as the wizard endpoints return it. */
export type SavedDoc = { _id: string } & Record<string, unknown>;

/** What GET conversations/mine and link-wizard return (the chat's restore state). */
export interface AssistantConversationState {
  conversationId: string;
  status: "building" | "materialized" | "abandoned";
  linkedEventId?: string | null;
  linkedContactInformationId?: string | null;
  messages: AssistantChatMessage[];
  draft: AssistantDraft;
  missing: string[];
}

export const getMyAssistantConversation = () =>
  http({ method: "GET", url: "event-assistant/conversations/mine", isToken: true });

/** What the wizard has when the chat is opened — the chat starts from it. */
export interface WizardSnapshot {
  /** Draft event the wizard already saved (step 2 done). */
  eventId?: string;
  /** Contact information doc the wizard already saved (step 1 done). */
  contactInformationId?: string;
  /** Step-1 values: the live form (`contactInfoForm`) or the saved doc. */
  contactInfo?: Record<string, unknown>;
  /** Step-2 values: the live form (`eventInfoForm`) or the saved doc. */
  eventInfo?: Record<string, unknown>;
  /** Client picked in the dropdown (staff); the event's userId when saved. */
  selectedClientId?: string | null;
}

/** True when the wizard holds anything worth carrying into the chat. */
export const wizardSnapshotHasData = (s?: WizardSnapshot | null): boolean =>
  !!s && !!(s.eventId || s.contactInformationId || s.contactInfo || s.eventInfo);

/**
 * Opens (or reuses) the conversation for what the wizard is building and
 * seeds the draft from it — typed step-1/2 values plus the sessions/positions
 * the wizard already saved. Links the conversation to the event so every later
 * sync UPDATES it instead of creating a second one.
 */
export const linkWizardToAssistant = (snapshot: WizardSnapshot) =>
  http({
    method: "POST",
    url: "event-assistant/conversations/link-wizard",
    data: snapshot,
    isToken: true,
  });

/** The wizard's Reset — abandons the chat too (the only thing that discards the build). */
export const abandonAssistantConversation = () =>
  http({
    method: "POST",
    url: "event-assistant/conversations/abandon",
    data: {},
    isToken: true,
  });

export interface AssistantSyncResult {
  success: boolean;
  message?: string;
  eventId: string;
  conversationId: string;
  contactInformation: SavedDoc | null;
  event: SavedDoc | null;
  /** False when the doc above is the previously saved one, returned only to keep its id (section still incomplete). */
  contactSaved: boolean;
  eventSaved: boolean;
  /** First wizard step that still needs the user (1 contact, 2 event, 3 sessions, 4 review). */
  resumeStep: 1 | 2 | 3 | 4;
  missing: string[];
  /** What could not be saved yet (incomplete) and stays in the draft. */
  skipped: string[];
  draft: AssistantDraft;
}

/**
 * "Continue to review": saves the COMPLETE draft into the event (created or
 * updated in place) — no submit. The response carries the contactInformation
 * and event docs so the caller can hydrate the wizard exactly like resuming a
 * draft, landing on the real step-4 review.
 */
export const materializeAssistantEvent = (conversationId: string) =>
  http({
    method: "POST",
    url: `event-assistant/conversations/${conversationId}/materialize`,
    data: {},
    isToken: true,
  });

/**
 * "Back to wizard": saves the draft as far as it is complete (contact → event
 * → sessions/positions that pass the wizard's gates) and returns the step to
 * resume on. Whatever is still incomplete stays in the draft.
 */
export const syncAssistantToWizard = (conversationId: string) =>
  http({
    method: "POST",
    url: `event-assistant/conversations/${conversationId}/sync-to-wizard`,
    data: {},
    isToken: true,
  });

/**
 * "Duplicate event": copies the event (contact, details, sessions, shifts)
 * into a NEW unsubmitted draft through the wizard's endpoints. Same response
 * shape as a sync (hydrate the wizard from it) plus `notCopied`.
 */
export const duplicateEventToWizard = (eventId: string) =>
  http({
    method: "POST",
    url: `event-assistant/duplicate/${eventId}`,
    data: {},
    isToken: true,
  });
